import { Injectable, OnDestroy, inject } from '@angular/core';
import { Client, IMessage } from '@stomp/stompjs';
import { Subject } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { AuthService } from '../../../authentication/application/services/auth.service';
import { Notification } from '../../application/entities/notification.entity';
import { NotificationRealtimePort } from '../../application/ports/notification-realtime.port';

interface UnreadCountPayload {
  unread: number;
}

/**
 * Cliente STOMP nativo (sin SockJS) sobre el endpoint WebSocket del backend.
 * La autenticación ocurre en el frame CONNECT vía el header `Authorization`.
 */
@Injectable({ providedIn: 'root' })
export class NotificationStompService implements NotificationRealtimePort, OnDestroy {
  private readonly authService = inject(AuthService);

  private readonly _notification$ = new Subject<Notification>();
  private readonly _unreadCount$ = new Subject<number>();

  readonly notification$ = this._notification$.asObservable();
  readonly unreadCount$ = this._unreadCount$.asObservable();

  private client: Client | null = null;

  connect(): void {
    const token = this.authService.getAccessToken();
    if (!token) return;
    if (this.client?.active) return;

    this.client = new Client({
      brokerURL: environment.wsUrl,
      connectHeaders: { Authorization: `Bearer ${token}` },
      reconnectDelay: 5000,
      heartbeatIncoming: 10000,
      heartbeatOutgoing: 10000,
      onConnect: () => {
        this.client?.subscribe('/user/queue/notifications', (message: IMessage) => {
          this._notification$.next(JSON.parse(message.body) as Notification);
        });
        this.client?.subscribe('/user/queue/notifications/unread-count', (message: IMessage) => {
          const payload = JSON.parse(message.body) as UnreadCountPayload;
          this._unreadCount$.next(payload.unread);
        });
      },
    });

    this.client.activate();
  }

  disconnect(): void {
    this.client?.deactivate();
    this.client = null;
  }

  ngOnDestroy(): void {
    this.disconnect();
  }
}
